import React, { PropsWithChildren } from 'react';
import { take, drop } from 'lodash';
import { Tooltip } from '@fiverr-private/tooltip';
import { renderChildren } from '@fiverr-private/ui_utils';
import { Container } from '@fiverr-private/layout_components';
import { Box, type Types } from '@fiverr-private/theme';
import type { AvatarGroupProps } from './types';
import { getAvatarGroupAvatarStyles } from './styles';
import LeftAvatars from './LeftAvatars';
import { OVERLAY_DISTANCE } from './constants';

const AvatarGroup = ({
  size = 'xs',
  maxAmount = 4,
  isAnimated = true,
  onClick,
  tooltipPosition,
  copies,
  children,
  disableTooltip = false,
  dataTestId,
  backgroundColor,
}: PropsWithChildren<AvatarGroupProps>) => {
  const childrenArray = React.Children.toArray(children);
  const isFits = maxAmount === childrenArray.length;
  const isClickable = typeof onClick === 'function';
  const mainAvatars = isFits ? childrenArray : take(childrenArray, maxAmount - 1);
  const leftAvatars = isFits ? [] : drop(childrenArray, maxAmount - 1);
  const hasAnimatedChildren = isAnimated && childrenArray.length > 1;
  const clickableInlineStyles = {
    ...(isClickable && {
      margin: '-4px',
    }),
  };

  const backgroundColorInternal: Types.BoxType['backgroundColor'] = isClickable
    ? { hover: 'grey_200', active: 'grey_300' }
    : undefined;

  return (
    <Container
      display="inlineFlex"
      cursor={isClickable ? 'pointer' : undefined}
      padding={isClickable ? '2' : undefined}
      borderRadius={isClickable ? 'circle' : undefined}
      backgroundColor={backgroundColor || backgroundColorInternal}
      onClick={onClick}
      data-testid={dataTestId}
      style={clickableInlineStyles}
    >
      {React.Children.map(mainAvatars, (child, index) => {
        const { displayName, username } = (child as React.ReactElement).props;
        const avatar = renderChildren(child, { size });
        const avatarStyles = getAvatarGroupAvatarStyles({
          hasAnimatedChildren,
          type: 'mainAvatars',
          index,
        });

        return (
          <Container
            style={
              index === 0
                ? undefined
                : {
                    marginLeft: -OVERLAY_DISTANCE,
                  }
            }
          >
            {disableTooltip ? (
              <Box {...avatarStyles}>{avatar}</Box>
            ) : (
              <Tooltip as="div" size="sm" content={displayName || username} position={tooltipPosition}>
                <Box {...avatarStyles}>{avatar}</Box>
              </Tooltip>
            )}
          </Container>
        );
      })}
      {leftAvatars.length > 0 && (
        <LeftAvatars
          tooltipPosition={tooltipPosition}
          copies={copies}
          borderColor="white"
          hasAnimatedChildren={hasAnimatedChildren}
          leftAvatars={leftAvatars}
          size={size}
        />
      )}
    </Container>
  );
};

AvatarGroup.displayName = 'AvatarGroup';

export default AvatarGroup;
